/*
*   Performs the BFS traversal of the `DirectedGraph` parameter, starting from the node with ID specified by the `rootID` parameter.
*   For each node, the `callback` function is called. 
*   
*   @Parameter{graph: DirectedGraph}: The DAG to traverse. 
*   @Parameter{rootID: Any}: The key value of a vertex in the graph, where to start the traversal from. Defaults to root.
*   @Parameter{shouldInclude: (DirectedEdge) => Bool}:  A function called for each edge to decide whether or not the edge should be visited. 
*                                                       Set to null to include every vertex.
*   @Parameter{sortEdges: (edgeA, edgeB) => Int}: A function that sorts the edges before visiting it. Set to null to ignore the sorting step.
*   @Parameter{callback: (vertexKey, vertexValue, isLeaf) => Void}: A function to run for each traversed node.
*
*   If there's no such vertex in the `graph` with id=`rootID`, no search is performed.
*/
export function BFS(graph, rootID = 'root', shouldInclude, sortEdges, callback) {
    let rootNode = graph.getVertex(rootID)

    if (!!rootNode) {
        let queue = [rootNode.key]

        while(queue.length > 0) {
            const currentVertex = queue.shift()
            const neighbors = graph.outgoingEdgesOf(currentVertex)

            callback(currentVertex, graph.getVertex(currentVertex).value, neighbors.length <= 0)

            
            if (!!shouldInclude) {
                neighbors.filter(shouldInclude)
            }

            if (!!sortEdges) {
                neighbors.sort(sortEdges)
            }

            neighbors.map(edge => edge.dest).forEach(neighbor => {
                queue.push(neighbor)
            })
        }
    } else {
        console.warn(`In \`function BFS(graph, rootID = 'root', shouldInclude, sortEdges, callback)\`, could not find a graph vertex with id ${rootID}. Aborting`)
    }
}


/*
*   Creates the GraphWiz DOT representation of the `graph` parameter, starting from the vertex with id=`rootID`.
*
*   @Parameter{graph: DirectedGraph}: The DAG to represent via DOT.
*   @Parameter{rootID: Any}: The identifier of the node of the subtree to represent.
*   @Parameter{graphName}: The name of the DOT representation of `graph`.
*   @Parameter{mapVertexKeyToString: (Any) => String}: A function that maps the key in the `graph` to the name of its DOT node.
*
*   @Returns{String}: A string representing `graph` in GraphWiz DOT notation.
*/
export function toDOT(graph, rootID = 'root', graphName = 'directed_graph', mapVertexKeyToString) {
    let rootNode = graph.getVertex(rootID)
    var DOT = `digraph "${graphName}" {
    
    `

    if (!!rootNode) {
        graph.vertexMap.forEach((directedVertex) => {
            DOT += `"${mapVertexKeyToString(directedVertex.key)}"; `
        })


        BFS(
            /*graph:*/ graph,
            /*node:*/ rootID,
            /*shouldInclude:*/ null,
            /*sortEdges:*/ null,
            /*callBack:*/ (vertexKey) => {
                if (graph.outgoingEdgesOf(vertexKey).length > 0) {
                    DOT += `"${mapVertexKeyToString(vertexKey)}" -> { `
                    
                    graph.outgoingEdgesOf(vertexKey).forEach(outgoingEdge => {
                        DOT +=`"${mapVertexKeyToString(outgoingEdge.dest)}"; `
                    })

                    DOT = DOT.substring(0, DOT.length - 2) + ` }
                    `
                }
            }
        )
    } else {
        console.warn(`In \`function toDOT(graph, rootID)\`, could not find a graph vertex with id ${rootID}. Aborting`)
    }

    DOT += `
    }
    `

    return DOT
}



/*
*   Performs the DFS traversal of the `graph` parameter, starting from the node with ID specified by the `rootID` parameter.
*   For each node, the `callback` function is called.
*   
*   @Parameter{graph: DirectedGraph}: The DAG to traverse.
*   @Parameter{rootID: Any}: The key value of a vertex in the graph, where to start the traversal from. Defaults to 'root'.
*   @Parameter{shouldInclude: (DirectedEdge) => Bool}:  A function called for each edge to decide whether or not the edge should be visited. 
*                                                       Set to null to include every vertex.
*   @Parameter{sortEdges: (edgeA, edgeB) => Int}: A function that sorts the edges before visiting it. Set to null to ignore the sorting step.
*   @Parameter{callback: (vertexKey, vertexValue, isLeaf, pathFromRoot) => Void}: A function to run for each traversed node.
*
*   If there's no such vertex in the `graph` with id=`rootID`, no search is performed.
*/
export function DFS(graph, rootID='root', shouldInclude, sortEdges, callback) {
    let rootNode = graph.getVertex(rootID)

    if (!!rootNode) {
        recursiveDFS(graph, { key: rootNode.key, value: rootNode.value }, shouldInclude, sortEdges, callback, [rootID])
    } else {
        console.warn(`In \`function DFS(graph, rootID='root', shouldInclude, sortEdges, callback)\`, could not find a graph vertex with id ${rootID}. Aborting`)
    }
}



/*
*   The helper function to recursively perform DFS, starting from 'node'.
*
*
*   @Parameter{graph: DirectedGraph}: The DAG to traverse.
*   @Parameter{node: {key: Any, value: Any}}: The (key, value) pair representing a vertex in the graph.
*   @Parameter{shouldInclude: (DirectedEdge) => Bool}:  A function called for each edge to decide whether or not the edge should be visited. 
*                                                       Set to null to include every vertex.
*   @Parameter{sortEdges: (edgeA, edgeB) => Int}: A function that sorts the edges before visiting it. Set to null to ignore the sorting step.
*   @Parameter{callback: (vertexKey, vertexValue, isLeaf, pathFromRoot) => Void}: A function to run for each traversed node.
*/
function recursiveDFS(graph, node, shouldInclude, sortEdges, callback, pathFromRoot) {
    callback(node.key, node.value, graph.outgoingEdgesOf(node.key).length <= 0, pathFromRoot)

    let neighbors = graph.outgoingEdgesOf(node.key)
        
    if (!!shouldInclude) {
        neighbors = neighbors.filter(shouldInclude) 
    }
     
    if (!!sortEdges) {
        neighbors.sort(sortEdges)
    }

    neighbors.forEach(edge => {
        let graphNodeForKey = graph.getVertex(edge.dest)

        if (!!graphNodeForKey) {
            recursiveDFS(
                /*graph:*/ graph, 
                /*node:*/ { key: edge.dest, value: graphNodeForKey.value },
                /*shouldInclude:*/ shouldInclude,
                /*sortEdges:*/ sortEdges,
                /*callBack:*/ callback,
                /*pathFromRoot:*/ [...pathFromRoot, edge.dest]
            )
        }
    })
}


/*
*   Returns an array whose elements are paths from the root to each leaf.
*
*   
*   @Parameter{graph: DirectedGraph}: The DAG to collect paths from.
*   @Parameter{rootID: Any}: The key pair representing the first node of the subgraph to collect.
*   @Parameter{shouldInclude: (DirectedEdge) => Bool}:  A function called for each edge to decide whether or not the edge should be visited. 
*                                                       Set to null to include every vertex.
*   @Parameter{sortEdges: (edgeA, edgeB) => Int}: A function that sorts the edges before visiting it. Set to null to ignore the sorting step.
*/
export function collectPaths(graph, rootID = 'root', shouldInclude, sortEdges) {
    if (!!graph) {
        const rootNode = graph.getVertex(rootID)

        if (!!rootNode) {
            let allPaths = []

            DFS(
                /*graph:*/ graph,
                /*rootID:*/ rootID, 
                /*shouldInclude:*/ shouldInclude,
                /*sortEdges*/ sortEdges,
                /*callBack*/ (vertexKey, vertexValue, isLeaf, pathFromRoot) => {
                    if (isLeaf) {  
                        allPaths.push(pathFromRoot)
                    }
                } 
            )

            return allPaths
        } else {
            console.warn(`In \`function collectPaths(graph, rootID = 'root', shouldInclude, sortEdges)\`, could not find a graph vertex with id ${rootID}. Aborting`)
            return null
        }
    } else {
        console.warn(`In \`function collectPaths(graph, rootID = 'root', shouldInclude, sortEdges)\`, \`graph\` parameter is null`)
        return undefined
    }
}


